import { create } from 'zustand';
import { DEFAULT_WALL_THICKNESS } from '../canvasConstants';
import type { ToolId, SymbolId } from '../types';
import type { CanvasTheme } from '../floorplanTheme';

export interface DraftPoint {
  x: number;
  y: number;
}

interface EditorState {
  activeTool: ToolId;
  gridVisible: boolean;
  snapEnabled: boolean;
  selectedIds: string[];
  canvasTheme: CanvasTheme;
  pendingSymbolId: SymbolId | null;
  /** Last symbol placed or picked, so the symbol tool always has something to drop. */
  lastSymbolId: SymbolId;
  /** Stroke weight used for the next wall. */
  wallThickness: number;
  /** Wall vertices placed during the current trace, before the wall is finished. */
  draftWallPoints: DraftPoint[] | null;
  setActiveTool: (tool: ToolId) => void;
  setGridVisible: (v: boolean) => void;
  setSnapEnabled: (v: boolean) => void;
  setSelectedIds: (ids: string[]) => void;
  setCanvasTheme: (theme: CanvasTheme) => void;
  toggleCanvasTheme: () => void;
  setPendingSymbol: (id: SymbolId | null) => void;
  setLastSymbol: (id: SymbolId) => void;
  setWallThickness: (px: number) => void;
  setDraftWallPoints: (pts: DraftPoint[] | null | ((prev: DraftPoint[] | null) => DraftPoint[] | null)) => void;
}

export const useEditorStore = create<EditorState>((set) => ({
  activeTool: 'select',
  gridVisible: true,
  snapEnabled: true,
  selectedIds: [],
  canvasTheme: 'light',
  pendingSymbolId: null,
  lastSymbolId: 'door',
  wallThickness: DEFAULT_WALL_THICKNESS,
  draftWallPoints: null,
  setActiveTool: (tool) =>
    set((s) => ({
      activeTool: tool,
      pendingSymbolId: tool === 'symbol' ? (s.pendingSymbolId ?? s.lastSymbolId) : s.pendingSymbolId,
    })),
  setGridVisible: (v) => set({ gridVisible: v }),
  setSnapEnabled: (v) => set({ snapEnabled: v }),
  setSelectedIds: (ids) => set({ selectedIds: ids }),
  setCanvasTheme: (theme) => set({ canvasTheme: theme }),
  toggleCanvasTheme: () => set((s) => ({ canvasTheme: s.canvasTheme === 'light' ? 'dark' : 'light' })),
  setPendingSymbol: (id) => set((s) => ({ pendingSymbolId: id, lastSymbolId: id ?? s.lastSymbolId })),
  setLastSymbol: (id) => set({ lastSymbolId: id }),
  setWallThickness: (px) => set({ wallThickness: px }),
  setDraftWallPoints: (pts) =>
    set((s) => ({
      draftWallPoints: typeof pts === 'function' ? pts(s.draftWallPoints) : pts,
    })),
}));
